import React, { useEffect, useRef, useCallback, useState } from "react"
import { useLocation } from "react-router-dom"
import PropTypes from "prop-types"

// //Import Scrollbar
import SimpleBar from "simplebar-react"

// MetisMenu
import MetisMenu from "metismenujs"
import withRouter from "../Common/withRouter"
import { Link } from "react-router-dom"

//i18n
import { withTranslation } from "react-i18next"
import { getUserPermissions, hasPagePermissionWithAdmin } from "../Permission/PermissionService"

const SidebarContent = props => {
  console.log('[SIDEBAR] SidebarContent component rendering');
  const ref = useRef()
  const [permissions, setPermissions] = useState([]);
  const [metisMenuInstance, setMetisMenuInstance] = useState(null);
  const metisMenuInstanceRef = useRef(null);
  const isInitializingRef = useRef(false);
  const path = useLocation();
  
  console.log(`[SIDEBAR] Current pathname: ${path.pathname}, Permissions count: ${permissions.length}`);

  // Function to initialize MetisMenu
  const initializeMetisMenu = useCallback((reason = 'unknown') => {
    // Prevent multiple simultaneous initializations
    if (isInitializingRef.current) {
      console.log(`[SIDEBAR] initializeMetisMenu already in progress, skipping - Reason: ${reason}`);
      return;
    }
    
    console.log(`[SIDEBAR] initializeMetisMenu called - Reason: ${reason}`);
    const sideMenu = document.getElementById("side-menu");
    if (!sideMenu) {
      console.warn('[SIDEBAR] Side menu element not found!');
      return;
    }
    
    // Check if sidebar is visible/expanded
    const isSidebarVisible = document.body.classList.contains('sidebar-enable') || 
                            !document.body.classList.contains('vertical-collpsed');
    console.log('[SIDEBAR] Sidebar visibility check - sidebar-enable:', document.body.classList.contains('sidebar-enable'), 
                'vertical-collpsed:', document.body.classList.contains('vertical-collpsed'),
                'isSidebarVisible:', isSidebarVisible);
    
    isInitializingRef.current = true;
    
    // Clean up existing instance if any
    if (metisMenuInstanceRef.current) {
      console.log('[SIDEBAR] Disposing existing MetisMenu instance');
      try {
        metisMenuInstanceRef.current.dispose();
      } catch (e) {
        console.warn('[SIDEBAR] Error disposing MetisMenu:', e);
      }
    }
    
    // Delay to ensure DOM is ready, especially when transitioning from condensed to expanded
    const delay = reason.includes('manually-opened') || reason.includes('hamburger') ? 200 : 150;
    setTimeout(() => {
      try {
        // Double-check menu element still exists
        const sideMenuCheck = document.getElementById("side-menu");
        if (!sideMenuCheck) {
          console.warn('[SIDEBAR] Side menu element disappeared during initialization!');
          isInitializingRef.current = false;
          return;
        }
        
        console.log('[SIDEBAR] Creating new MetisMenu instance');
        const instance = new MetisMenu("#side-menu");
        metisMenuInstanceRef.current = instance;
        setMetisMenuInstance(instance);
        console.log('[SIDEBAR] MetisMenu instance created successfully', instance);
        console.log('[SIDEBAR] MetisMenu triggerArr length:', instance.triggerArr ? instance.triggerArr.length : 'N/A');
        isInitializingRef.current = false;
      } catch (e) {
        console.error("[SIDEBAR] Error initializing MetisMenu:", e);
        isInitializingRef.current = false;
      }
    }, delay);
  }, []);

  // Hard reset helper to clear classes and force a clean MetisMenu instance
  const hardResetMetisMenu = useCallback((reason = 'unknown-reset') => {
    const sideMenuElement = document.getElementById("side-menu");
    if (!sideMenuElement) return;
    try {
      if (metisMenuInstanceRef.current && typeof metisMenuInstanceRef.current.dispose === 'function') {
        metisMenuInstanceRef.current.dispose();
      }
    } catch (e) {
      console.warn('[SIDEBAR] Error disposing MetisMenu during hard reset:', e);
    }
    // Remove mm-* state classes to avoid stale state
    try {
      sideMenuElement.querySelectorAll('.mm-active').forEach(el => el.classList.remove('mm-active'));
      sideMenuElement.querySelectorAll('.mm-show').forEach(el => el.classList.remove('mm-show'));
      sideMenuElement.querySelectorAll('.has-arrow.mm-active').forEach(el => el.classList.remove('mm-active'));
    } catch (e) {
      // no-op
    }
    // Recreate
    initializeMetisMenu(`hard-reset-${reason}`);
  }, [initializeMetisMenu]);

  useEffect(() => {
    console.log('[SIDEBAR] useEffect for permissions loading triggered');
    const loadPermissions = async () => {
      try {
        console.log('[SIDEBAR] Loading permissions in sidebar');
        const userPermissions = await getUserPermissions();
        console.log('[SIDEBAR] Loaded permissions:', userPermissions);
        console.log(`[SIDEBAR] Setting permissions state (${userPermissions.length} items)`);
        setPermissions(userPermissions);
        
        // Store permissions in localStorage so spa pages can access them
        localStorage.setItem('permissions', JSON.stringify(userPermissions));
        console.log('[SIDEBAR] Permissions stored in localStorage');
        
        // Initialize MetisMenu after permissions are loaded
        setTimeout(() => {
          console.log('[SIDEBAR] Timeout completed - initializing MetisMenu');
          initializeMetisMenu('initial-permissions-loaded');
        }, 100);
      } catch (error) {
        console.error("[SIDEBAR] Error loading permissions:", error);
      }
    };
    loadPermissions();
  }, [initializeMetisMenu]);

  // Function to check if a menu item should be shown
  const shouldShowMenuItem = (route) => {
    const hasPermission = hasPagePermissionWithAdmin(permissions, route);
    return hasPermission;
  };

  // Function to get the appropriate dashboard route based on permissions
  const getDashboardRoute = () => {
    const hasDashboardPermission = hasPagePermissionWithAdmin(permissions, '/Dashboard');
    const hasSpaDashboardPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaEmployeeDashboard');
    const hasSpaManagerFinancePermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaManagerFinanceDashboard');
    
    // If user has HR Dashboard permission (admin), show regular Dashboard
    if (hasDashboardPermission) {
      return '/Dashboard';
    }
    // If user has Spa Manager & Finance Dashboard permission, show that
    if (hasSpaManagerFinancePermission) {
      return '/GmsSpaManagerFinanceDashboard';
    }
    // If user only has Spa Employee Dashboard permission, show that
    if (hasSpaDashboardPermission) {
      return '/GmsSpaEmployeeDashboard';
    }
    // Fallback to default dashboard for users without specific permissions
    return '/DefaultDashboard';
  };

  const activateParentDropdown = useCallback(item => {
    item.classList.add("active")
    const parent = item.parentElement
    const parent2El = parent.childNodes[1]

    if (parent2El && parent2El.id !== "side-menu") {
      parent2El.classList.add("mm-show")
    }

    if (parent) {
      parent.classList.add("mm-active")
      const parent2 = parent.parentElement

      if (parent2) {
        parent2.classList.add("mm-show") // ul tag

        const parent3 = parent2.parentElement // li tag

        if (parent3) {
          parent3.classList.add("mm-active") // li
          parent3.childNodes[0].classList.add("mm-active") //a
          const parent4 = parent3.parentElement // ul
          if (parent4) {
            parent4.classList.add("mm-show") // ul
            const parent5 = parent4.parentElement
            if (parent5) {
              parent5.classList.add("mm-show") // li
              parent5.childNodes[0].classList.add("mm-active") // a tag
            }
          }
        }
      }
      scrollElement(item)
      return false
    }
    scrollElement(item)
    return false
  }, [])

  const removeActivation = items => {
    for (var i = 0; i < items.length; ++i) {
      var item = items[i]
      const parent = items[i].parentElement

      if (item && item.classList.contains("active")) {
        item.classList.remove("active")
      }
      if (parent) {
        const parent2El =
          parent.childNodes && parent.childNodes.lenght && parent.childNodes[1]
            ? parent.childNodes[1]
            : null
        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show")
        }

        parent.classList.remove("mm-active")
        const parent2 = parent.parentElement

        if (parent2) {
          parent2.classList.remove("mm-show")

          const parent3 = parent2.parentElement
          if (parent3) {
            parent3.classList.remove("mm-active") // li
            parent3.childNodes[0].classList.remove("mm-active")

            const parent4 = parent3.parentElement // ul
            if (parent4) {
              parent4.classList.remove("mm-show") // ul
              const parent5 = parent4.parentElement
              if (parent5) {
                parent5.classList.remove("mm-show") // li
                parent5.childNodes[0].classList.remove("mm-active") // a tag
              }
            }
          }
        }
      }
    }
  }

  const activeMenu = useCallback(() => {
    const pathName = path.pathname
    console.log(`[SIDEBAR] activeMenu called - Current path: ${pathName}`);
    const ul = document.getElementById("side-menu")
    if (!ul) {
      console.warn('[SIDEBAR] Side menu element not found in activeMenu!');
      return;
    }
    
    let matchingMenuItem = null
    const items = ul.getElementsByTagName("a")
    console.log(`[SIDEBAR] Found ${items.length} menu items`);
    removeActivation(items)
    console.log('[SIDEBAR] Removed all activations');

    for (let i = 0; i < items.length; ++i) {
      if (pathName === items[i].pathname) {
        matchingMenuItem = items[i]
        console.log(`[SIDEBAR] Found matching menu item for path: ${pathName}`, matchingMenuItem);
        break
      }
    }
    if (matchingMenuItem) {
      console.log('[SIDEBAR] Activating parent dropdown for matching item');
      activateParentDropdown(matchingMenuItem)
    } else {
      console.log(`[SIDEBAR] No matching menu item found for path: ${pathName}`);
    }
  }, [path.pathname, activateParentDropdown])

  useEffect(() => {
    console.log('[SIDEBAR] Component mounted - recalculating scrollbar');
    if (ref.current) {
      ref.current.recalculate()
    }
  }, [])

  useEffect(() => {
    console.log('[SIDEBAR] Initial activeMenu call');
    activeMenu()
  }, [activeMenu])

  useEffect(() => {
    console.log(`[SIDEBAR] Route changed to: ${path.pathname}`);
    console.log('[SIDEBAR] Running activeMenu and re-initializing MetisMenu');
    window.scrollTo({ top: 0, behavior: "smooth" })
    activeMenu()
    
    // Re-initialize MetisMenu after route change to fix hover issues
    // This ensures MetisMenu's event handlers are properly attached after navigation
    // Increased delay to ensure DOM is fully updated
    setTimeout(() => {
      if (!isInitializingRef.current) {
        hardResetMetisMenu(`route-change-${path.pathname}`);
      } else {
        console.log('[SIDEBAR] Skipping route-change initialization - already initializing');
      }
    }, 300);
  }, [activeMenu, hardResetMetisMenu, path.pathname])

  // Re-initialize MetisMenu when sidebar type changes (e.g., condensed <-> default)
  // This ensures menu works properly when sidebar is manually toggled
  useEffect(() => {
    if (props.sidebarType) {
      console.log(`[SIDEBAR] Sidebar type changed to: ${props.sidebarType}`);
      // Wait for CSS transitions to complete after sidebar type change
      const timeoutId = setTimeout(() => {
        const sideMenu = document.getElementById("side-menu");
        if (sideMenu && !isInitializingRef.current) {
          console.log('[SIDEBAR] Hard resetting MetisMenu due to sidebar type change');
          hardResetMetisMenu(`sidebar-type-changed-${props.sidebarType}`);
        }
      }, 400); // Longer delay to ensure CSS transitions complete
      
      return () => clearTimeout(timeoutId);
    }
  }, [props.sidebarType, hardResetMetisMenu])

  // Re-initialize MetisMenu when sidebar is manually opened (especially after auto-hide)
  useEffect(() => {
    let timeoutId = null;
    let lastSidebarEnabled = false;
    
    console.log('[SIDEBAR] Setting up listener for sidebar-enable class changes');
    
    // Get initial state
    if (document.body) {
      lastSidebarEnabled = document.body.classList.contains('sidebar-enable');
      console.log(`[SIDEBAR] Initial sidebar-enable state: ${lastSidebarEnabled}`);
    }
    
    // Listen specifically for sidebar-enable class changes (when user clicks hamburger)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const body = document.body;
          const currentSidebarEnabled = body.classList.contains('sidebar-enable');
          
          // Check if sidebar-enable state actually changed
          if (currentSidebarEnabled !== lastSidebarEnabled) {
            console.log(`[SIDEBAR] sidebar-enable changed: ${lastSidebarEnabled} -> ${currentSidebarEnabled}`);
            lastSidebarEnabled = currentSidebarEnabled;
            
            // When sidebar is manually opened (sidebar-enable becomes true), re-initialize MetisMenu
            if (currentSidebarEnabled) {
              console.log('[SIDEBAR] Sidebar manually opened - re-initializing MetisMenu');
              console.log('[SIDEBAR] Current body classes:', body.className);
              
              // Clear any pending timeouts
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
              
              // Re-initialize when sidebar is opened - use longer delay to ensure DOM is fully updated
              // This is especially important when transitioning from condensed to expanded mode
              timeoutId = setTimeout(() => {
                // Double-check that sidebar is still enabled and menu element exists and is visible
                const sideMenu = document.getElementById("side-menu");
                const sidebarElement = document.querySelector('.vertical-menu');
                const isSidebarVisible = sidebarElement && 
                                        (window.getComputedStyle(sidebarElement).display !== 'none' &&
                                         window.getComputedStyle(sidebarElement).visibility !== 'hidden');
                
                if (sideMenu && document.body.classList.contains('sidebar-enable') && !isInitializingRef.current) {
                  // If sidebar element exists, check if it's visible; otherwise proceed anyway
                  if (sidebarElement && !isSidebarVisible) {
                    console.log('[SIDEBAR] Sidebar element exists but not visible yet, waiting a bit more...');
                    // Wait a bit more for CSS transitions to complete
                    setTimeout(() => {
                      if (!isInitializingRef.current) {
                        console.log('[SIDEBAR] Executing MetisMenu hard reset after visibility check');
                        hardResetMetisMenu('sidebar-manually-opened-delayed');
                      }
                    }, 200);
                  } else {
                    console.log('[SIDEBAR] Executing MetisMenu hard reset for manual sidebar open');
                    console.log('[SIDEBAR] Side menu element found, re-initializing...');
                    hardResetMetisMenu('sidebar-manually-opened');
                  }
                } else {
                  console.warn('[SIDEBAR] Cannot re-initialize: sideMenu exists:', !!sideMenu, 
                              'sidebar-enabled:', document.body.classList.contains('sidebar-enable'), 
                              'isInitializing:', isInitializingRef.current,
                              'sidebar visible:', isSidebarVisible);
                }
              }, 300); // Increased delay to ensure DOM transition is complete
            }
          }
        }
      });
    });

    // Observe body class changes
    if (document.body) {
      const currentClasses = document.body.className;
      console.log(`[SIDEBAR] Current body classes: ${currentClasses}`);
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Also listen for click events on the hamburger button as a backup
    // Use event delegation on document to catch clicks even if button isn't ready yet
    const handleHamburgerClick = (e) => {
      // Check if the click was on the hamburger button or its children
      const target = e.target;
      const hamburgerButton = document.getElementById('vertical-menu-btn');
      if (hamburgerButton && (target === hamburgerButton || hamburgerButton.contains(target))) {
        console.log('[SIDEBAR] Hamburger button clicked');
        console.log('[SIDEBAR] Current sidebar-enable state before click:', document.body.classList.contains('sidebar-enable'));
        
        // Wait for the class toggle to complete, then re-initialize
        // Use longer delay to ensure DOM is fully updated, especially when transitioning from condensed mode
        setTimeout(() => {
          const isSidebarEnabled = document.body.classList.contains('sidebar-enable');
          const sideMenu = document.getElementById("side-menu");
          const sidebarElement = document.querySelector('.vertical-menu');
          const isSidebarVisible = sidebarElement && 
                                  (window.getComputedStyle(sidebarElement).display !== 'none' &&
                                   window.getComputedStyle(sidebarElement).visibility !== 'hidden');
          
          console.log('[SIDEBAR] After click - sidebar-enabled:', isSidebarEnabled, 
                     'sideMenu exists:', !!sideMenu, 'sidebar visible:', isSidebarVisible);
          
          if (isSidebarEnabled && sideMenu && !isInitializingRef.current) {
            // Check if sidebar is visible, if not wait a bit more
            if (sidebarElement && !isSidebarVisible) {
              console.log('[SIDEBAR] Sidebar not fully visible yet, waiting for CSS transition...');
              setTimeout(() => {
                if (document.body.classList.contains('sidebar-enable') && !isInitializingRef.current) {
                  console.log('[SIDEBAR] Sidebar opened via hamburger (delayed) - hard resetting MetisMenu');
                  hardResetMetisMenu('hamburger-button-clicked-delayed');
                }
              }, 200);
            } else {
              console.log('[SIDEBAR] Sidebar opened via hamburger - hard resetting MetisMenu');
              hardResetMetisMenu('hamburger-button-clicked');
            }
          } else if (!isSidebarEnabled) {
            console.log('[SIDEBAR] Sidebar was closed, skipping re-initialization');
          } else {
            console.warn('[SIDEBAR] Cannot re-initialize after hamburger click:', {
              isSidebarEnabled,
              sideMenuExists: !!sideMenu,
              isInitializing: isInitializingRef.current
            });
          }
        }, 350); // Increased delay to ensure DOM transition is complete
      }
    };
    
    // Use event delegation on document body to catch clicks
    console.log('[SIDEBAR] Adding click listener to document for hamburger button');
    document.body.addEventListener('click', handleHamburgerClick);

    return () => {
      console.log('[SIDEBAR] Cleaning up sidebar listeners');
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      observer.disconnect();
      document.body.removeEventListener('click', handleHamburgerClick);
    };
  }, [initializeMetisMenu]);

  // Cleanup MetisMenu instance on unmount
  useEffect(() => {
    return () => {
      console.log('[SIDEBAR] Component unmounting - disposing MetisMenu');
      if (metisMenuInstanceRef.current) {
        try {
          metisMenuInstanceRef.current.dispose();
          console.log('[SIDEBAR] MetisMenu disposed successfully');
        } catch (e) {
          console.warn('[SIDEBAR] Error disposing MetisMenu on unmount:', e);
        }
      }
    };
  }, []);

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300
      }
    }
  }

  // Define menu items with their routes
  // 1. Employees & HR
  const employeeDirectoryMenuItems = [
    { route: "/GmsEmployee", label: "Employee" },
    { route: "/GmsExpiringDocuments", label: "Expiring Documents" },
  ];

  const attendanceLeaveMenuItems = [
    // { route: "/GmsAbsentDays", label: "Absent Days" },
    { route: "/GmsEmployeeLeave", label: "Leave Request" },
    { route: "/GmsLeaveType", label: "Leave Types" },
    { route: "/GmsLeaveCalculator", label: "Leave Calculator" },
    { route: "/GmsEmployeeLeaveCredit", label: "Update Leave Balances" },
    { route: "/GmsAnnualLeaveCredit", label: "Annual Leave Credit Processing", icon: "bx bx-calendar-check" },
  ];

  const payrollManagementMenuItems = [
    { route: "/GmsSalaryAdvance", label: "Salary Advance" },
    { route: "/GmsPayrollProcessing", label: "Payroll Processing" },
    { route: "/GmsSalaryRevision", label: "Salary Revision", icon: "bx bx-money" },
  ];

  // 2. Customers & Appointments
  const customerManagementMenuItems = [
    { route: "/GmsCustomer", label: "New Customer" },
  ];

  const bookingManagementMenuItems = [
    { route: "/GmsSpaCalendar", label: "Booking Calendar", icon: "bx bx-calendar" },
    { route: "/GmsBooking", label: "New Booking", icon: "bx bx-calendar-check" },
  ];

  const membershipsMenuItems = [
    { route: "/GmsMembership", label: "Membership Plans & Renewals", icon: "bx bx-id-card" },
  ];

  // 3. POS & Billing
  const posBillingMenuItems = [
    { route: "/GmsBilling", label: "Billing / Invoice Creation", icon: "bx bx-receipt" },
  ];

  // Day Closing submenu items
  const dayClosingMenuItems = [
    { route: "/GmsDailyClosing", label: "X Report", icon: "bx bx-calendar-check" },
    { route: "/GmsZReport", label: "Z Report", icon: "bx bx-file-blank" },
  ];

  // Receipts submenu items
  const receiptsMenuItems = [
    { route: "/GmsCustomerReceipts", label: "Customer Receipts", icon: "bx bx-money" },
    { route: "/GmsCashDeposit", label: "Cash Deposit", icon: "bx bx-wallet" },
  ];


  // 4. Products & Services
  const serviceCatalogueMenuItems = [
    { route: "/GmsSpaService", label: "Add / Edit Services", icon: "bx bx-spa" },
    { route: "/GmsSpaCategory", label: "Category", icon: "bx bx-category" },
    { route: "/GmsSpaSubCategory", label: "Sub Category", icon: "bx bx-subdirectory-right" },
  ];

  const packagesMenuItems = [
    { route: "/GmsSpaPackages", label: "Packages", icon: "bx bx-package" },
    { route: "/GmsSpaPackageSeries", label: "Series Packages", icon: "bx bx-collection" },
  ];

  const productsInventoryMenuItems = [
    { route: "/GmsProductManagement", label: "Add / Edit Products" },
    { route: "/GmsRoom", label: "Room" },
    { route: "/GmsEquipment", label: "Equipment", icon: "bx bx-wrench" },
    { route: "/GmsStockTransfer", label: "Stock Transfer", icon: "bx bx-transfer" },
    { route: "/GmsReceiveStockTransfer", label: "Receive Stock Transfer", icon: "bx bx-check-circle" },
  ];

  // 5. Performance & Reports
  const employeePerformanceMenuItems = [
    { route: "/GmsDaybook", label: "Day Book" },
    { route: "/GmsSalesbook", label: "Sales Book" },
  ];

  const operationalReportsMenuItems = [
    { route: "/GmsInvoiceReport", label: "Sales Reports" },
    { route: "/GmsBookingReport", label: "Booking Reports" },
    { route: "/GmsStockReport", label: "Stock Report", icon: "bx bx-bar-chart" },
    { route: "/GmsDailySummary", label: "Daily Summary Report" },
  ];

  const hrReportsMenuItems = [
    { route: "/GmsSalaryAdvanceTransactionReport", label: "Salary Advance Transaction Report" },
    { route: "/GmsPayrollHistoryReport", label: "Payroll History Report" },
    { route: "/GmsPayrollDetailsHistoryReport", label: "Payroll Details History Report" },
    { route: "/GmsEmployeeReport", label: "Employee Report" },
  ];

  // 6. Settings & Administration
  const businessSettingsMenuItems = [
    // { route: "/GmsGroup", label: "Group" },
    { route: "/GmsCompany", label: "Group" },
    { route: "/GmsBusiness", label: "Company" },
    { route: "/GmsBranch", label: "Branch" },
    { route: "/GmsDepartment", label: "Department" },
    { route: "/GmsDesignation", label: "Designation" },
    { route: "/GmsTax", label: "Tax", icon: "bx bx-calculator" },
  ];

  const userManagementMenuItems = [
    { route: "/GmsUser", label: "Access Control" },
    { route: "/GmsStockLockManagement", label: "Stock Lock Management", icon: "bx bx-lock" },
  ];

  const notificationsMenuItems = [
    { route: "/GmsNotifications", label: "All Notifications" },
  ];

  // Banks submenu items
  const banksMenuItems = [
    { route: "/GmsBank", label: "Bank", icon: "bx bx-building" },
    { route: "/GmsBankAccount", label: "Bank Account", icon: "bx bx-credit-card" },
  ];

  // Suppliers & Stock menu items
  const supplierManagementMenuItems = [
    { route: "/GmsSupplier", label: "Add/Edit Supplier" },
    { route: "/GmsSupplierCategory", label: "Supplier Category" },
  ];

  const purchaseReturnsMenuItems = [
    { route: "/GmsPurchaseOrder", label: "Purchase Order" },
    { route: "/GmsPurchase", label: "Purchase Invoice" },
  ];

  // 8. Others
  const othersMenuItems = [
    { route: "/GmsService", label: "Service Management" },
    { route: "/GmsActivityManagement", label: "Activity Management" },
    { route: "/GmsActivityUpdated", label: "Activities", icon: "bx bx-refresh" },
  ];


  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            <li className="menu-title">{props.t("Menu")}</li>

            {/* Dashboard */}
            <li>
              <Link to={getDashboardRoute()}>
                <i className="bx bx-home-circle"></i>
                <span>{props.t("Dashboard")}</span>
              </Link>
            </li>

            {/* 1. Employees & HR Menu */}
            {(employeeDirectoryMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              attendanceLeaveMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              payrollManagementMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-user-circle"></i>
                  <span>{props.t("Employees & HR")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Employee Directory Submenu */}
                  {employeeDirectoryMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Employee Directory")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {employeeDirectoryMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Attendance & Leave Submenu */}
                  {attendanceLeaveMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Attendance & Leave")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {attendanceLeaveMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Payroll Management Submenu */}
                  {payrollManagementMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Payroll Management")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {payrollManagementMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 2. Customers & Booking Menu */}
            {(customerManagementMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              bookingManagementMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              membershipsMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-user"></i>
                  <span>{props.t("Customers & Booking")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Customer Management Submenu */}
                  {customerManagementMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Customer Management")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {customerManagementMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Booking Management Submenu */}
                  {bookingManagementMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Booking Management")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {bookingManagementMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Memberships Submenu */}
                  {membershipsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Memberships")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {membershipsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 3. POS & Billing Menu */}
            {(posBillingMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              receiptsMenuItems.some(item => shouldShowMenuItem(item.route)) ||
              dayClosingMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-receipt"></i>
                  <span>{props.t("POS & Billing")}</span>
                </Link>
                <ul className="sub-menu">
                  <li>
                    <Link to="/#" className="has-arrow">
                      <span>{props.t("Point of Sale")}</span>
                    </Link>
                    <ul className="sub-menu">
                      {posBillingMenuItems.map((item) => 
                        shouldShowMenuItem(item.route) && (
                          <li key={item.route}>
                            <Link to={item.route}>{props.t(item.label)}</Link>
                          </li>
                        )
                      )}
                    </ul>
                  </li>
                  {receiptsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Receipts")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {receiptsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  {dayClosingMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Day Closing")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {dayClosingMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 4. Products & Services Menu */}
            {(serviceCatalogueMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              packagesMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              productsInventoryMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-spa"></i>
                  <span>{props.t("Products & Services")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Service Catalogue Submenu */}
                  {serviceCatalogueMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Service Catalogue")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {serviceCatalogueMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Packages Submenu */}
                  {packagesMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Packages")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {packagesMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Products & Inventory Submenu */}
                  {productsInventoryMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Products & Inventory")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {productsInventoryMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 5. Suppliers & Stock Menu */}
            {(supplierManagementMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              purchaseReturnsMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-package"></i>
                  <span>{props.t("Suppliers & Stock")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Supplier Management Submenu */}
                  {supplierManagementMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Supplier Management")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {supplierManagementMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Purchase & Returns Submenu */}
                  {purchaseReturnsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Purchase & Returns")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {purchaseReturnsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 6. Performance & Reports Menu */}
            {(employeePerformanceMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              operationalReportsMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              hrReportsMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-file"></i>
                  <span>{props.t("Performance & Reports")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Employee Performance Submenu */}
                  {employeePerformanceMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Employee Performance")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {employeePerformanceMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Operational Reports Submenu */}
                  {operationalReportsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Operational Reports")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {operationalReportsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* HR Reports Submenu */}
                  {hrReportsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("HR Reports")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {hrReportsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 7. Settings & Administration Menu */}
            {(businessSettingsMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              userManagementMenuItems.some(item => shouldShowMenuItem(item.route)) || 
              notificationsMenuItems.some(item => shouldShowMenuItem(item.route)) ||
              banksMenuItems.some(item => shouldShowMenuItem(item.route))) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-cog"></i>
                  <span>{props.t("Settings & Administration")}</span>
                </Link>
                <ul className="sub-menu">
                  {/* Business Settings Submenu */}
                  {businessSettingsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Business Settings")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {businessSettingsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* User Management Submenu */}
                  {userManagementMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("User Management")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {userManagementMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Notifications Submenu */}
                  {notificationsMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Notifications")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {notificationsMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                  
                  {/* Banks Submenu */}
                  {banksMenuItems.some(item => shouldShowMenuItem(item.route)) && (
                    <li>
                      <Link to="/#" className="has-arrow">
                        <span>{props.t("Banks")}</span>
                      </Link>
                      <ul className="sub-menu">
                        {banksMenuItems.map((item) => 
                          shouldShowMenuItem(item.route) && (
                            <li key={item.route}>
                              <Link to={item.route}>{props.t(item.label)}</Link>
                            </li>
                          )
                        )}
                      </ul>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* 8. Others Menu */}
            {othersMenuItems.some(item => shouldShowMenuItem(item.route)) && (
              <li>
                <Link to="/#" className="has-arrow">
                  <i className="bx bx-dots-horizontal-rounded"></i>
                  <span>{props.t("Others")}</span>
                </Link>
                <ul className="sub-menu">
                  {othersMenuItems.map((item) => 
                    shouldShowMenuItem(item.route) && (
                      <li key={item.route}>
                        <Link to={item.route}>{props.t(item.label)}</Link>
                      </li>
                    )
                  )}
                </ul>
              </li>
            )}


          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
  sidebarType: PropTypes.string,
}

export default withRouter(withTranslation()(SidebarContent))
